import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Box,
  EmptyServicesListIcon,
  getDuration,
  List,
  PaginationBar,
  Responsive,
  SearchInput,
  Text,
  useDebounce,
  usePaginationParams,
  useResponsive,
} from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import UploadButton from '@/components/controls/DropScan/UploadButton/UploadButton';
import UploadFileErrorPopup from '@/components/controls/DropScan/UploadFileErrorPopup';
import { LMN_UPLOAD_STORE_KEY, LMNPopupMode } from '@/modules/MyDocuments/myDocuments.types';
import useGetLettersOfMedicalNecessityQuery from '@/modules/MyDocuments/queries/useGetLettersOfMedicalNecessity.query';
import { useFilters } from '@/utils';

import LettersOfMedicalNecessityPopup from '../LettersOfMedicalNecessityPopup/LettersOfMedicalNecessityPopup';

import LettersOfMedicalNecessityDeleteModal from './LettersOfMedicalNecessityDeleteModal';
import LettersOfMedicalNecessityListItem from './LettersOfMedicalNecessityListItem';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_CHARS_COUNT = 3;

const LettersOfMedicalNecessityList = () => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { page, perPage, setPerPage, setPage, searchString } = usePaginationParams(DEFAULT_PAGE_SIZE);

  const [isFileUploadErrorModalVisible, setIsFileUploadErrorModalVisible] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<FileError | undefined>();
  const [deleteLetterId, setDeleteLetterId] = useState<string | undefined>(undefined);
  const [uploadedHighlight, setUploadedHighlight] = useState<string>('');
  const [viewLmnItemId, setViewLmnItemId] = useState<string | undefined>(undefined);
  const [isUploadLmnPopupVisible, setIsUploadLmnPopupVisible] = useState(false);
  const [isViewLmnPopupVisible, setIsViewLmnPopupVisible] = useState(false);
  const [modalMode, setModalMode] = useState<LMNPopupMode>(LMNPopupMode.VIEW);

  const { filtersValues, setFilterValue } = useFilters<{ searchString: string }>(
    { searchString },
    { autoClear: true }
  );
  const debouncedSearch = useDebounce(
    filtersValues.searchString.length >= SEARCH_CHARS_COUNT ? filtersValues.searchString : ''
  );

  const { lettersOfMedicalNecessity, total, isLoading } = useGetLettersOfMedicalNecessityQuery({
    page,
    perPage,
    searchString: debouncedSearch,
  });

  useEffect(() => {
    if (uploadedHighlight) {
      setTimeout(
        () => {
          setUploadedHighlight('');
        },
        getDuration({
          seconds: 4,
        })
      );
    }
  }, [uploadedHighlight]);
  const handleUpload = useCallback(() => {
    setIsUploadLmnPopupVisible(true);
  }, []);

  const history = useHistory();

  const handleView = useCallback(
    (id?: string) => {
      if (isMobile) {
        history.push(ROUTES.MY_DOCUMENTS_LMN_VIEW_MOBILE(id));
      } else {
        setViewLmnItemId(id);
        setModalMode(LMNPopupMode.VIEW);
        setIsViewLmnPopupVisible(true);
      }
    },
    [isMobile, history]
  );

  const handleEditFromView = useCallback(() => {
    setModalMode(LMNPopupMode.EDIT);
  }, []);

  const handleEdit = useCallback(
    (id?: string) => {
      if (isMobile) {
        history.push(ROUTES.MY_DOCUMENTS_LMN_EDIT_MOBILE(id));
      } else {
        setViewLmnItemId(id);
        setModalMode(LMNPopupMode.EDIT);
        setIsViewLmnPopupVisible(true);
      }
    },
    [isMobile, history]
  );

  const handleDelete = useCallback((id?: string) => {
    setDeleteLetterId(id);
  }, []);

  const deletedItem = useMemo(() => {
    return lettersOfMedicalNecessity?.find((letter) => letter.id === deleteLetterId);
  }, [lettersOfMedicalNecessity, deleteLetterId]);

  const onFileUploadError = useCallback((error?: FileError) => {
    setFileUploadError(error);
    setIsFileUploadErrorModalVisible(true);
  }, []);

  const onCloseFileUploadError = useCallback(() => {
    setFileUploadError(undefined);
    setIsFileUploadErrorModalVisible(false);
  }, []);

  const children = useMemo(() => {
    if (isLoading) {
      return <List.Preloader testId="lmn-list_preloader" />;
    }

    if (!lettersOfMedicalNecessity?.length) {
      return (
        <Box
          height={isMobile ? '360px' : '420px'}
          align="center"
          justify="center"
          data-testid="lmn-list_placeholder"
        >
          <EmptyServicesListIcon size="72px" color="iconSecondary" />
          <Text size="medium" color="textDisabled" weight={700} margin={{ top: 'spacing12' }}>
            {t('No results have been found')}
          </Text>
        </Box>
      );
    }

    return lettersOfMedicalNecessity?.map((letterOfMedicalNecessity) => (
      <LettersOfMedicalNecessityListItem
        key={letterOfMedicalNecessity?.id}
        id={letterOfMedicalNecessity?.id}
        isHighlighted={uploadedHighlight === letterOfMedicalNecessity?.id}
        name={letterOfMedicalNecessity?.name}
        description={letterOfMedicalNecessity?.description}
        claims={letterOfMedicalNecessity?.claims}
        validFrom={letterOfMedicalNecessity?.validFrom}
        validTo={letterOfMedicalNecessity?.validTo}
        fileUrl={letterOfMedicalNecessity?.thumbnailUrl}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    ));
  }, [
    isLoading,
    lettersOfMedicalNecessity,
    isMobile,
    t,
    uploadedHighlight,
    handleView,
    handleEdit,
    handleDelete,
  ]);

  useEffect(() => {
    setPage(1);
  }, [setPage, filtersValues.searchString]);

  return (
    <>
      <Box
        data-testId="lmn-list_wrapper"
        pad={isMobile ? 'spacing16' : 'spacing24'}
        background="module"
        round="container1Round"
        {...isMobile ? { margin: { horizontal: 'spacing2' } } : {}}
      >
        {isMobile ? (
          <Box direction="column">
            <Box direction="row" align="center" justify="between">
              <Box>
                <Text weight={700} data-testId="lmn-list_total">
                  {t('Files')}: {total}
                </Text>
              </Box>

              <Box direction="row" align="center">
                <UploadButton
                  onUpload={handleUpload}
                  onFileUploadError={onFileUploadError}
                  style={{ width: '172px' }}
                  storeKey={LMN_UPLOAD_STORE_KEY}
                />
              </Box>
            </Box>
            <Box margin={{ top: 'spacing16' }}>
              <SearchInput
                testId="lmn-list"
                value={filtersValues.searchString}
                onChange={setFilterValue.searchString}
                placeholder={t('Search for file name or description')}
                hasSearchingHistory
              />
            </Box>
          </Box>
        ) : (
          <Box direction="row" align="center" justify="between">
            <Text weight={700} data-testId="lmn-list_total">
              Files: {total}
            </Text>

            <Box gap="spacing12" direction="row" align="center">
              <Box width={{ min: '305px' }}>
                <SearchInput
                  testId="lmn-list"
                  value={filtersValues.searchString}
                  onChange={setFilterValue.searchString}
                  placeholder={t('Search for file name or description')}
                  hasSearchingHistory
                />
              </Box>

              <UploadButton
                onUpload={handleUpload}
                onFileUploadError={onFileUploadError}
                storeKey={LMN_UPLOAD_STORE_KEY}
              />
            </Box>
          </Box>
        )}

        <Box margin={{ top: isMobile ? 'spacing8' : 'spacing24' }}>
          <List>
            <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
              <List.Header>
                <List.Header.Item flex={0.6}></List.Header.Item>
                <List.Header.Item flex={1}>{t('File name')}</List.Header.Item>
                <List.Header.Item flex={1.8}>{t('Description')}</List.Header.Item>
                <List.Header.Item flex={1}>{t('Linked claims')}</List.Header.Item>
                <List.Header.Item flex={1}>{t('Dates')}</List.Header.Item>
                <List.Header.Item flex={0.5} textAlign="end">
                  {t('Actions')}
                </List.Header.Item>
              </List.Header>
            </Responsive>

            {children}
          </List>
        </Box>

        <PaginationBar
          total={total}
          page={page}
          onChangePage={setPage}
          pageSize={perPage}
          onChangePageSize={setPerPage}
        />

        <LettersOfMedicalNecessityDeleteModal deletedItem={deletedItem} onSetId={setDeleteLetterId} />
      </Box>
      {isUploadLmnPopupVisible ? (
        <LettersOfMedicalNecessityPopup
          mode={LMNPopupMode.UPLOAD}
          onSuccess={setUploadedHighlight}
          onSetVisible={setIsUploadLmnPopupVisible}
          isMobile={isMobile}
        />
      ) : null}
      {isFileUploadErrorModalVisible && (
        <UploadFileErrorPopup
          fileError={fileUploadError}
          onClose={onCloseFileUploadError}
        />
      )}
      {isViewLmnPopupVisible ? (
        <LettersOfMedicalNecessityPopup
          itemId={viewLmnItemId}
          onSetVisible={setIsViewLmnPopupVisible}
          mode={modalMode || LMNPopupMode.VIEW}
          onEdit={handleEditFromView}
          onDelete={handleDelete}
        />
      ) : null}
    </>
  );
};

export default LettersOfMedicalNecessityList;
